/**
 * GlucoseFlagBanner — the entry-time red-flag shown when a reading trips a threshold.
 *
 * Levels come from the classifier (glucoseUnits.classifyGlucoseMgDl, mirror of the engine
 * classifyGlucose). The emergency (urgent) low is styled red and leads with first-aid; the
 * caution low and the high are amber. Copy lives in strings.ts. This is the whole extent of
 * the red-flag wiring for G7 — inline warning only, nothing persisted (Stuart, 2026-07-22).
 */
import React from 'react';
import type { GlucoseWarningLevel } from './glucoseUnits';
import { GLUCOSE_STRINGS as S } from './strings';

const COPY: Record<Exclude<GlucoseWarningLevel, 'none'>, { title: string; body: string; urgent: boolean }> = {
  hypo_emergency: { title: S.flagEmergencyTitle, body: S.flagEmergencyBody, urgent: true },
  hypo_caution: { title: S.flagCautionTitle, body: S.flagCautionBody, urgent: false },
  hyper_attention: { title: S.flagHighTitle, body: S.flagHighBody, urgent: false },
};

export const GlucoseFlagBanner: React.FC<{ level: GlucoseWarningLevel }> = ({ level }) => {
  if (level === 'none') return null;
  const { title, body, urgent } = COPY[level];
  const tone = urgent
    ? 'border-red-300 bg-red-50 text-red-800'
    : 'border-amber-300 bg-amber-50 text-amber-800';
  return (
    <div className={`rounded-xl border px-4 py-3 ${tone}`} role={urgent ? 'alert' : 'status'}>
      <p className="text-sm font-semibold">{title}</p>
      <p className="mt-1 text-sm">{body}</p>
    </div>
  );
};

export default GlucoseFlagBanner;
